import { api } from "@/api"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export type Category = DeepReadonly<{
  id: string
  name: string
  parentCategoryId: string
  color: string | null
  path: string[]
  namePath: string
  timeslotIds: string[]
  pinnedPosition: number | null
}>

export const listCategories = async (): Promise<
  Category[]
> => {
  const response = await api.get<Category[]>(
    "/products/categories/"
  )
  return response.body
}

export type CategoryFilters = Partial<{
  search: string
  parentCategoryId: string
}>

export const useCategories = (
  filters: MaybeRef<CategoryFilters> = {}
) => {
  const { data: allCategories, ...rest } = useQuery(
    [BASE_CATEGORIES_QUERY_KEY],
    () => listCategories(),
    {
      staleTime: 60 * 60 * 1000,
    }
  )

  const data = computed(() => {
    if (!allCategories.value) return []
    const filtered =
      unref(filters).parentCategoryId === undefined
        ? allCategories.value
        : allCategories.value?.filter(
            (category) =>
              category.parentCategoryId ===
              unref(filters).parentCategoryId
          )
    const search = unref(filters).search
    const searched = search
      ? searchBy(filtered, ["name"], search)
      : filtered
    return searched
  })

  const asOptions = computed(() =>
    data.value
      .filter(({ id }) => id !== null)
      .map(({ id, namePath }) => ({
        value: id,
        label: namePath,
      }))
  )

  const getById = (
    id: MaybeRef<string | null | undefined>
  ) =>
    computed(() => {
      if (unref(id) === null || unref(id) === undefined)
        return

      return allCategories.value?.find(
        (category) => category.id === id
      )
    })

  const getLabelForValue = (value: string) =>
    computed(
      () =>
        asOptions.value.find(
          (option) => option.value === value
        )?.label || ""
    )

  return {
    allCategories: readonly(allCategories),
    data: readonly(data),
    ...rest,
    asOptions,
    getById,
    getLabelForValue,
  }
}
